import { Maybe, MaybeUndef } from "types/UtilityTypes";

import BackgroundOverlay from "components/loading/BackgroundOverlay";
import CloseButton from "components/buttons/CloseButton";
import ColorClass from "types/enums/ColorClass";
import ColorValue from "types/enums/ColorValue";
import Header2 from "components/text/Header2";
import OutsideClickHideModal from "components/modal/OutsideClickHideModal";
import ReactDOM from "react-dom";
import joinClasses from "utils/joinClasses";
import styles from "css/modal/GenericModal.module.css";
import { useEffect } from "react";

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
  className?: string;
  footer?: MaybeUndef<JSX.Element>;
  hideCloseButton?: boolean;
  hideWithVisibility?: boolean;
  isShown: boolean;
  onHide: () => void;
  title?: string | JSX.Element;
};

export default function GenericModal({
  children,
  className,
  hideCloseButton = false,
  hideWithVisibility = false,
  footer,
  isShown,
  onHide,
  title,
}: Props): Maybe<JSX.Element> {
  // Disable outer scroll when modal is visible.
  // See https://stackoverflow.com/questions/9538868/prevent-body-from-scrolling-when-a-modal-is-opened/69005672#69005672.
  useEffect(() => {
    if (isShown) {
      const width = document.body.clientWidth;
      document.body.style.overflowY = "hidden";
      document.body.style.width = `${width}px`;
    } else {
      document.body.style.overflowY = "scroll";
      document.body.style.width = `auto`;
    }

    return () => {
      document.body.style.overflowY = "scroll";
      document.body.style.width = `auto`;
    };
  }, [isShown]);

  if (!isShown && !hideWithVisibility) {
    return null;
  }

  return (
    <>
      {isShown && <BackgroundOverlay />}
      {ReactDOM.createPortal(
        <div
          className={styles.container}
          style={{
            top: window.scrollY,
            visibility: !isShown && hideWithVisibility ? "hidden" : "visible",
          }}
        >
          <OutsideClickHideModal
            className={styles.outsideClick}
            hideModal={onHide}
          >
            {!hideCloseButton && (
              <div className={styles.closeRow}>
                <CloseButton colorValue={ColorValue.White} onClick={onHide} />
              </div>
            )}
            <div className={joinClasses(styles.modal, className)}>
              {title != null && (
                <div className={styles.title}>
                  {typeof title === "string" ? (
                    <Header2 colorClass={ColorClass.Primary} textAlign="center">
                      {title}
                    </Header2>
                  ) : (
                    title
                  )}
                </div>
              )}
              {children}
              {footer != null && <div className={styles.footer}>{footer}</div>}
            </div>
          </OutsideClickHideModal>
        </div>,
        document.body
      )}
    </>
  );
}
